import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export type OrderDocumentType = 'DELIVERY_TICKET' | 'OTHER';

export interface TicketBox {
  field: string;
  page: string;
  x: string;
  y: string;
  w: string;
  h: string;
}

export interface DeliveryTicketExtraction {
  loadNumber: string;
  ticketNumber: string;
  deliveryDate: string;
  poNumber: string;
  productName: string;
  carrierName: string;
  driverName: string;
  netTons: string;
  netPounds: string;
  grossTons: string;
  tareTons: string;
  weighmaster: string;
  customerName: string;
  boxes: TicketBox[];
}

export interface TicketPreviewPage {
  page: number;
  url: string;
}

export interface OrderDocument {
  id: string;
  orderItemId: string | null;
  type: OrderDocumentType;
  fileName: string;
  fileUrl: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  extractedData?: DeliveryTicketExtraction | null;
  extractedAt?: string | null;
  uploadedAt: string;
  uploadedBy?: { firebaseUid: string | null; name?: string | null; email?: string | null } | null;
  // Rasterized preview page images (delivery tickets only).
  previewPages?: TicketPreviewPage[];
}

export interface ExtractDeliveryTicketResult {
  document: OrderDocument;
  extraction: DeliveryTicketExtraction;
  delivered: { quantityTons: number | null; loadNumberMatches: boolean };
  previewPages: TicketPreviewPage[];
}

export function useOrderDocuments(orderId?: string | null) {
  return useQuery({
    queryKey: ['order-documents', orderId],
    enabled: !!orderId,
    queryFn: async () => {
      const { data } = await api.get(`/organization/orders/${orderId}/documents`);

      return data;
    },
  });
}

type UploadArg = File | { file: File; type?: OrderDocumentType; orderItemId?: string };

export function useUploadOrderDocument(orderId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    // Accepts a bare File (existing callers) or { file, type, orderItemId }.
    mutationFn: async (arg: UploadArg) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const file = arg instanceof File ? arg : arg.file;
      const type = arg instanceof File ? undefined : arg.type;
      const orderItemId = arg instanceof File ? undefined : arg.orderItemId;

      const formData = new FormData();
      formData.append('file', file);
      if (type) formData.append('type', type);
      if (orderItemId) formData.append('orderItemId', orderItemId);

      const { data } = await api.post(`/organization/orders/${orderId}/documents`, formData);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['order-documents', orderId],
      });

      queryClient.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

// AI-read a delivery ticket; the server stores the parse and returns it plus a
// resolved delivered-tons value and whether the ticket's load number matches.
export function useExtractOrderDocument(orderId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const { data } = await api.post<ExtractDeliveryTicketResult>(
        `/organization/orders/${orderId}/documents/${documentId}/extract`,
        {}
      );

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useDeleteOrderDocument(orderId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const { data } = await api.delete(`/organization/orders/${orderId}/documents/${documentId}`);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['order-documents', orderId],
      });

      queryClient.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useDownloadOrderDocument(orderId?: string | null) {
  return useMutation({
    mutationFn: async ({ documentId, fileName }: { documentId: string; fileName: string }) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const response = await api.get(
        `/organization/orders/${orderId}/documents/${documentId}/download`,
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');

      anchor.href = objectUrl;
      anchor.download = fileName || 'document';

      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(objectUrl);

      return {
        documentId,
        fileName,
      };
    },
  });
}

export function usePreviewOrderDocument(orderId?: string | null) {
  return useMutation<Blob, Error, string>({
    mutationFn: async (documentId) => {
      if (!orderId) {
        throw new Error('Missing order id');
      }

      const response = await api.get<Blob>(
        `/organization/orders/${encodeURIComponent(orderId)}/documents/${encodeURIComponent(documentId)}/download`,
        { responseType: 'blob' }
      );

      // Explicit PDF type allows the blob: URL to render in an iframe even if
      // the storage service served the original PDF as application/octet-stream.
      return new Blob([response.data], { type: 'application/pdf' });
    },
  });
}
